'use client';

import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  title?: string;
  description?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('ErrorBoundary caught:', error, info);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      const { title = 'Etwas ist schiefgelaufen', description = 'Ein unerwarteter Fehler ist aufgetreten. Bitte lade die Seite neu.' } = this.props;
      return (
        <div className="punk-card p-6 border-red-800/50 flex flex-col items-center text-center gap-4">
          <AlertTriangle size={32} className="text-red-400" />
          <div>
            <h3 className="text-sm font-700 text-white mb-1">{title}</h3>
            <p className="text-xs text-muted">{description}</p>
            {this.state.error && (
              <p className="text-xs mono text-red-400 mt-2">{this.state.error.message}</p>
            )}
          </div>
          <button onClick={this.handleReset} className="btn-ghost text-xs">
            <RefreshCw size={12} />
            Erneut versuchen
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export { ErrorBoundary };

interface ApiErrorMessageProps {
  title?: string;
  description?: string;
  onRetry?: () => void;
}

const ApiErrorMessage: React.FC<ApiErrorMessageProps> = ({
  title = 'API-Fehler',
  description = 'Die Verbindung zur KI-API ist fehlgeschlagen.',
  onRetry,
}) => {
  return (
    <div
      className="flex items-start gap-3 p-4 rounded border border-red-800/50 bg-red-950/20"
      role="alert"
      aria-live="assertive"
    >
      <AlertTriangle size={16} className="text-red-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-600 text-red-400">{title}</p>
        <p className="text-xs text-muted mt-0.5">{description}</p>
      </div>
      {onRetry && (
        <button
          onClick={onRetry}
          className="btn-ghost text-xs flex-shrink-0"
          aria-label="Erneut versuchen"
        >
          <RefreshCw size={12} />
          Retry
        </button>
      )}
    </div>
  );
};

export { ApiErrorMessage };